import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setSettlementTokenAddress } from "store/userSlice";
import {
  networkTypeSelector,
  settlementTokenAddressSelector,
} from "store/userSliceHelpers";
import {
  resolveToken,
  sameToken,
  tokenLabel,
  tokenParam,
  tokensFor,
  TokenOption,
} from "utils/tokens";

export interface UseSettlementTokenReturn {
  /** What this portal offers on this network. Local — no fetch, no loading state. */
  tokens: TokenOption[];
  selected: TokenOption | undefined;
  /** `?token=` value for the API. */
  param: string | undefined;
  label: string;
  /** Whether there is a real choice — drives whether the picker renders. */
  canSwitch: boolean;
  select: (address: string) => void;
}

// The token list is a CLIENT constant, so `selected` is available on first render and every
// consumer agrees without coordination. Whether the backend can actually settle in it is a
// separate question — see useTokenSupport.
export function useSettlementToken(): UseSettlementTokenReturn {
  const dispatch = useDispatch();
  const networkType = useSelector(networkTypeSelector);
  const stored = useSelector(settlementTokenAddressSelector);

  const tokens = useMemo(() => tokensFor(networkType), [networkType]);
  const selected = useMemo(() => resolveToken(tokens, stored), [tokens, stored]);

  const select = useCallback(
    (address: string) => {
      // Picking the DEFAULT stores nothing. Only a deliberate deviation is persisted, so someone
      // who never deviated follows whatever the portal's default currently is — instead of being
      // pinned to a stored copy of the default that was in force when they first clicked.
      const pickedDefault = tokens.some(
        (t) => t.isDefault && sameToken(t.address, address),
      );
      dispatch(setSettlementTokenAddress(pickedDefault ? undefined : address));
    },
    [dispatch, tokens],
  );

  return {
    tokens,
    selected,
    param: tokenParam(selected),
    label: tokenLabel(selected),
    canSwitch: tokens.length > 1,
    select,
  };
}
